/**
 * RegisterPage - User registration page
 * Handles email/password registration with proper validation and error handling
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { ObjectSchema } from 'yup';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { FormField } from '@components/forms/FormField';
import { PasswordStrengthMeter } from '@components/forms/PasswordStrengthMeter';
import { Button } from '@components/ui/Button';
import { useAuthOperations } from '@hooks/useApi';
import { RegisterForm } from '@defs/forms';
import { TermsModal } from '@components/modals/TermsModal';

// ============================================================================
// Validation Schema
// ============================================================================

const registerSchema = yup.object({
  name: yup
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre no puede tener más de 50 caracteres')
    .trim()
    .optional()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .default(undefined),
  email: yup
    .string()
    .email('Email inválido')
    .required('Email es requerido')
    .trim(),
  password: yup
    .string()
    .min(12, 'La contraseña debe tener al menos 12 caracteres')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/,
      'La contraseña debe incluir mayúsculas, minúsculas y números',
    )
    .test(
      'no-leading-trailing-space',
      'No uses espacios al inicio o al final',
      (value) => (value ? value.trim() === value : true),
    )
    .required('Contraseña es requerida'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Confirmar contraseña es requerido'),
  termsAccepted: yup
    .boolean()
    .oneOf([true], 'Debes aceptar los Términos y Condiciones')
    .required(),
}) as ObjectSchema<ExtendedRegisterForm>;

// ============================================================================
// Styled Components
// ============================================================================

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const HelperRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  font-size: 0.95rem;
  text-align: center;

  a {
    color: var(--brand-600);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--brand-600);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const TermsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-top: 1rem;
`;

const TermsCheckbox = styled.input`
  margin-top: 0.2rem;
`;

const TermsLabel = styled.label`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

const TermsLinkButton = styled.button`
  border: none;
  background: none;
  color: var(--brand-600);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  font-weight: 600;

  &:hover {
    color: var(--brand-700);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FieldError = styled.span`
  display: block;
  color: var(--danger-600);
  font-size: 0.875rem;
  margin-top: 0.35rem;
`;

// ============================================================================
// Extended Form Interface
// ============================================================================

interface ExtendedRegisterForm extends RegisterForm {
  confirmPassword: string;
  termsAccepted: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser, isLoading } = useAuthOperations();
  const [isTermsOpen, setIsTermsOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ExtendedRegisterForm>({
    resolver: yupResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: {
      termsAccepted: false,
    },
  });

  // Watch password to show requirements
  const password = watch('password');

  const onSubmit = async (data: ExtendedRegisterForm) => {
    try {
      // Prepare registration data (exclude confirmPassword and undefined name)
      const registrationData: RegisterForm = {
        ...(data.name ? { name: data.name } : {}),
        email: data.email,
        password: data.password,
      };

      await registerUser(registrationData);

      toast.success(
        'Cuenta creada exitosamente. Revisa tu email para verificar tu cuenta.',
        { duration: 6000 },
      );

      // Redirect to login page
      navigate('/auth/login', {
        state: {
          message:
            'Cuenta creada. Por favor verifica tu email antes de iniciar sesión.',
        },
      });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Registration error:', error);
      const apiError = error as {
        status?: number;
        detail?:
          | string
          | { code?: string; provider?: string; google_subject?: string };
        message?: string;
      };

      // Handle specific error cases
      if (apiError.status === 409) {
        if (
          typeof apiError.detail === 'object' &&
          apiError.detail?.code === 'link_required' &&
          apiError.detail.provider === 'google'
        ) {
          toast.error(
            'Ya hay una cuenta con Google asociada a este email. Vamos a vincularla.',
          );

          const params = new URLSearchParams({
            email: data.email,
            provider: 'google',
          });

          if (apiError.detail.google_subject) {
            params.set('sub', apiError.detail.google_subject);
          }

          navigate(`/auth/link-account?${params.toString()}`);
          return;
        }

        if (
          typeof apiError.detail === 'object' &&
          apiError.detail?.code === 'google_only'
        ) {
          toast.error(
            'Ya registraste esta cuenta con Google. Inicia sesión con Google o recupera tu contraseña.',
          );
          navigate('/auth/login', {
            state: {
              message:
                'Usa el botón de Google o “¿Olvidaste tu contraseña?” para crear una contraseña nueva.',
              email: data.email,
            },
          });
          return;
        }

        toast.error(
          'Ya existe una cuenta con este email. ¿Quieres iniciar sesión?',
        );
      } else if (apiError.status === 422) {
        toast.error(
          'Datos inválidos. Verifica la información e intenta de nuevo.',
        );
      } else if (apiError.detail) {
        toast.error(
          typeof apiError.detail === 'string'
            ? apiError.detail
            : 'Error al crear la cuenta. Intenta de nuevo.',
        );
      } else {
        toast.error('Error al crear la cuenta. Intenta de nuevo.');
      }
    }
  };

  return (
    <>
      <h1 className="auth-title">Crear cuenta</h1>
      <p className="auth-subtitle">
        Únete para llevar el control de tu sostenibilidad
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField
          label="Nombre"
          name="name"
          type="text"
          placeholder="Tu nombre (opcional)"
          autoComplete="given-name"
          register={register}
          error={errors.name}
          helperText="Opcional - nos ayuda a personalizar tu experiencia"
        />

        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="tu@email.com"
          required
          autoComplete="email"
          register={register}
          error={errors.email}
        />

        <FormField
          label="Contraseña"
          name="password"
          type="password"
          placeholder="••••••••"
          required
          autoComplete="new-password"
          register={register}
          error={errors.password}
        />

        <PasswordStrengthMeter password={password} />

        <FormField
          label="Confirmar contraseña"
          name="confirmPassword"
          type="password"
          placeholder="••••••••"
          required
          autoComplete="new-password"
          register={register}
          error={errors.confirmPassword}
        />

        <TermsRow>
          <TermsCheckbox
            id="termsAccepted"
            type="checkbox"
            {...register('termsAccepted')}
            aria-describedby={errors.termsAccepted ? 'terms-error' : undefined}
          />
          <TermsLabel htmlFor="termsAccepted">
            Acepto los
            <TermsLinkButton
              type="button"
              onClick={() => setIsTermsOpen(true)}
              aria-expanded={isTermsOpen}
            >
              Términos y Condiciones
            </TermsLinkButton>
          </TermsLabel>
        </TermsRow>
        {errors.termsAccepted && (
          <FieldError id="terms-error">
            {errors.termsAccepted.message}
          </FieldError>
        )}

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            Crear cuenta
          </Button>
        </FormActions>
      </form>

      <HelperRow>
        <span>
          ¿Ya tienes cuenta? <Link to="/auth/login">Iniciar sesión</Link>
        </span>
      </HelperRow>

      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </>
  );
};
